import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { request } from "services/api";

/**
 * Default function
 *
 * @returns all campaigns
 */
export default () =>
  useQuery(["campaignList"], async () => {
    const response = await request.get("/campaign/getCampaign");
    if (response.data.success) {
      return response.data.data;
    }

    return [];
  });

export const updateInititalStock = async(campaign) => {
  try{
    const response = await request.put("/campaign/updateInitialStockCampaign", campaign);    
    if (response.data.success) {
      return response.data;
    }
    return response;
  } catch (error) {
    // Handle error
    throw error;
  }
};

export const updateInititalSolde = async (campaign) => {
  try{
      const response = await request.put("/campaign/updateInitialSoldeCampaign", campaign);
      if (response.data.success) {
        return response.data;
      }
      return response;
    } catch (error) {
      throw error;
    }
};

export const getCampaign = (farmId,year) => {
  return useQuery(
    ["campaign", "farm", farmId, year],
    async () => {
      const response = await request.get(
        `/campaign/getCampaign?farmId=${farmId}&year=${year}`
      );

      if (response.data.success) {
        return response.data.data;
      }

      return [];
    },
    {
      enabled: farmId > 0,
    }
  );
};

export const getCampaignById = (farmId,year,id) => {
  return useQuery(
    ["campaign", "farm", farmId, year,id],
    async () => {
      const response = await request.get(
        `/campaign/getCampaign?farmId=${farmId}&year=${year}&campaignId:${id}`
      );
      if (response.data.success) {
        return response.data.data;
      }
      return [];
    },
    {
      enabled: farmId > 0,
    }
  );
};

/**
 * Add campaign
 *
 * @param {*} campaign
 * @returns campaign
 */
export const addCampaign = () => {
  return useMutation({
    mutationFn: async (payload) => {
      try {
        const result = await request.post("/campaign/addCampaign", {
          farmId: payload.farmId,
        });

        if (result.data.success) {
          localStorage.removeItem('campagn-data');
          localStorage.removeItem('campagn-data-last-step');
          const campaignId = result.data.data;
          const campaign = preparedCampaign({ campaignId, ...payload });
          return campaign;
        }

        throw result;
      } catch (error) {
        throw error;
      }
    },
  });
};

/**
 * Update campaign
 * @param {*} campaign
 * @returns void
 */
export const updateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaign) => {
      const response = await request.put("/campaign/updateCampaign", campaign);
      if (response.data.success) {
        return response.data.data;
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaignList"] });
    },
  });
};

/**
 *
 * @param {*} data
 * @returns server campaign object
 */
function preparedCampaign(data) {

  const { repair, acquisition, materialsEquipment } = data.equipments.reduce(
    (accObj, item) => {
      accObj.materialsEquipment = [2] // TODO: get material id

      if (item.type === "Acquisition") {
        accObj.acquisition = [
          ...accObj.acquisition,
          {
            materialsEquipmentId: item.materialsEquipmentId,
            quantity: item.data[item.type].qty,
            cost: item.data[item.type].cost,
          },
        ];
      } else if (item.type === "Réparation") {
        accObj.repair = [
          ...accObj.repair,
          {
            materialsEquipmentId: item.materialsEquipmentId,
            quantity: item.data[item.type].qty,
            cost: item.data[item.type].cost,
            premises: item.data[item.type].lieux,
            period: item.data[item.type].période,
          },
        ];
      }

      return accObj;
    },
    { repair: [], acquisition: [], materialsEquipment: [] }
  );
  return {
    campaignId: data.campaignId,
    farmId: data.farmId,
    productsYouGrow: data.crops.map((c) => c.id),
    isprimaryproduct: "",
    primaryproduct: [],
    quantityToDo: data.crops.map((crop) => ({
      product: crop.id,
      quantity: crop.seedArea,
    })),
    expectedProduction: data.crops.map((crop) => ({
      product: crop.id,
      quantity: crop.expectedProduction,
    })),
    purposeOfProduction: data.crops.map((crop) => ({
      product: crop.id,
      self: crop.purpose.self,
      donation: crop.purpose.donate,
      sale: crop.purpose.sell,
    })),
    expectedProfit: data.crops.map((crop) => ({
      product: crop.id,
      amount: crop.margins,
    })),
    season: data.season === "Grande saison" ? "GREAT" : "SMALL",
    labor: {
      laborCount: data.laborCost.labor,
      laborCountP: data.laborCost.laborP,
      laborCost: data.laborCost.qty,
      laborCostP: data.laborCost.qtyP,
      mof: data.laborCost.qty,
    },
    seedRequirement: data.seedRequirements.map((item) => ({
      product: item.productId, 
      type: item.type,
      quantity: item.qty,
      cost: item.cost,
    })),
    fertilizerRequirement: data.fertilizerRequirements.map((item) => ({
      campaignId: data.campaignId, 
      productId: item.productId, 
      type: item.type,
      quantity: item.qty,
      cost: item.cost,
    })),
    phytoProductRequirement: data.phyto.map((item) => ({
      campaignId: data.campaignId, 
      productId: item.productId, 
      type: item.type,
      quantity: item.qty,
      cost: item.cost,
    })),
    materialsEquipment,
    acquisitionAndRepair: {
      acquisition,
      repair,
    },
    nonOperatingNeed: data.noneOperatingNeeds.map((item) => ({
      nonOpId: item.materialsEquipmentId,
      cost: item.cost,
    })),
    totalCost: "",
    operatorAssets: {
      operating: data.operatorAssetType.map((item) => ({
        article: item.name,
        assetType: item.assetTypeId,
        cost: item.cost,
      })),
      nonOperating: data.noneOperatingNeeds.map((item) => ({
        article: item.name,
        assetType: item.assetTypeId,
        cost: item.cost,
      })),
    },
    probablePeriodOfMobilization: {
      startDate: data.monitization.from,
      endDate: data.monitization.to,
    },
    otherProduction: data.otherProductions.map((item)=>({
      name: item.name,
      area: parseInt(item.seedArea),
    }))
  };
}
